/**
 * Braslab spectrophotometer CSV -> reflection curve.
 *
 * Mirrors the lab's "Conversor Braslab CSV" (and the Octave script it comes
 * from): the CSV has a free-form preamble, then a data section that starts at
 * a "Lambda;Reference;Mesure" or "Lambda;Reflexion" header line, with ";"
 * separated columns and decimal commas. We keep wavelength and reflection,
 * trim to MIN_NM..MAX_NM and turn the reflection fraction into a percentage,
 * which is what the other spectrophotometers' files already contain.
 */

const HEADERS = ['Lambda;Reference;Mesure', 'Lambda;Reflexion'];

/** Wavelength range kept, in nm (same defaults as the converter). */
export const MIN_NM = 350;
export const MAX_NM = 780;

/** Braslab exports reflection as a 0..1 fraction; Cairo works in %. */
const FACTOR = 100;

export type BraslabResult = { wave: number[][] } | { error: string };

/** Whether `text` looks like a Braslab CSV (has one of its data headers). */
export function isBraslabCsv(text: string): boolean {
    return text.split(/\r?\n/).some(line => HEADERS.some(h => line.includes(h)));
}

/** Converts a Braslab CSV into [[nm, %], ...] rows, or explains why it can't. */
export function braslabToWave(text: string): BraslabResult {
    const lines = text.split(/\r?\n/);
    const start = lines.findIndex(line => HEADERS.some(h => line.includes(h)));
    if (start === -1) {
        return { error: 'No se encontró la sección de datos' };
    }

    const wave: number[][] = [];
    for (const raw of lines.slice(start + 1)) {
        const parts = raw.trim().split(';');
        if (parts.length < 2) continue;
        const nm = toNum(parts[0]);
        const r = toNum(parts[1]);
        if (Number.isFinite(nm) && Number.isFinite(r) && nm >= MIN_NM && nm <= MAX_NM) {
            wave.push([nm, r * FACTOR]);
        }
    }

    if (wave.length === 0) {
        return { error: `No hay datos entre ${MIN_NM} y ${MAX_NM} nm` };
    }
    return { wave };
}

function toNum(s: string): number {
    s = s.trim();
    return s === '' ? NaN : Number(s.replace(',', '.'));
}
